import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Timer, Play, Square, X } from 'lucide-react';
import { tempsAPI, dossiersAPI } from '../lib/api';
import { toast } from '../lib/toast';

const LS_KEY = 'adalia_timer';
const pad = (n) => String(n).padStart(2, '0');
const fmt = (ms) => {
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  return h ? `${h}:${pad(m)}:${pad(sec)}` : `${pad(m)}:${pad(sec)}`;
};

/**
 * QuickTimer — chrono de saisie de temps, accessible depuis le header partout.
 * Démarrable d'un clic, persistant (localStorage, survit aux navigations/refresh),
 * et à l'arrêt il enregistre la saisie (tempsAPI.create) avec dossier/description/
 * facturable. Les revenus d'un cabinet = des heures captées : zéro friction.
 */
export default function QuickTimer() {
  const [open, setOpen] = useState(false);
  const [startedAt, setStartedAt] = useState(null);   // ms epoch, null = arrêté
  const [now, setNow] = useState(() => Date.now());
  const [dossierId, setDossierId] = useState('');
  const [description, setDescription] = useState('');
  const [facturable, setFacturable] = useState(true);
  const [dossiers, setDossiers] = useState([]);
  const [saving, setSaving] = useState(false);
  const ref = useRef(null);

  const running = startedAt != null;
  const elapsed = running ? Math.max(0, now - startedAt) : 0;

  // Restauration depuis localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) {
        const s = JSON.parse(raw);
        if (s.startedAt) {
          setStartedAt(s.startedAt);
          setDossierId(s.dossierId || '');
          setDescription(s.description || '');
          setFacturable(s.facturable !== false);
        }
      }
    } catch { /* ignore */ }
  }, []);

  // Tic d'horloge quand le chrono tourne
  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [running]);

  // Persistance
  const persist = useCallback((patch) => {
    const base = { startedAt, dossierId, description, facturable, ...patch };
    if (base.startedAt) localStorage.setItem(LS_KEY, JSON.stringify(base));
    else localStorage.removeItem(LS_KEY);
  }, [startedAt, dossierId, description, facturable]);

  // Fermeture au clic extérieur
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // Charge les dossiers à l'ouverture (pour le sélecteur)
  useEffect(() => {
    if (open && dossiers.length === 0) {
      dossiersAPI.list().then((r) => setDossiers(r.data || [])).catch(() => {});
    }
  }, [open, dossiers.length]);

  const start = () => {
    const t = Date.now();
    setStartedAt(t); setNow(t);
    persist({ startedAt: t });
  };

  const reset = () => {
    setStartedAt(null); setDescription(''); setDossierId('');
    localStorage.removeItem(LS_KEY);
  };

  const stopAndSave = async () => {
    const minutes = Math.max(1, Math.round(elapsed / 60000));
    setSaving(true);
    try {
      const payload = { duree_minutes: minutes, facturable };
      if (dossierId) payload.dossier_id = dossierId;
      if (description) payload.description = description;
      await tempsAPI.create(payload);
      toast.success(`Temps enregistré · ${fmt(elapsed)}`);
      reset();
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de l'enregistrement du temps");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="h-9 px-2.5 rounded-lg flex items-center gap-2 transition-colors"
        style={{
          background: running ? 'rgba(201,162,39,0.12)' : 'var(--bg-tertiary)',
          border: running ? '1px solid rgba(201,162,39,0.30)' : '1px solid transparent',
        }}
        title="Chrono de saisie de temps"
        data-testid="quick-timer-button"
      >
        {running ? (
          <motion.span animate={{ opacity: [1, 0.4, 1] }} transition={{ duration: 2, repeat: Infinity }} className="w-1.5 h-1.5 rounded-full" style={{ background: '#C9A227' }} />
        ) : (
          <Timer className="w-4 h-4" style={{ color: 'var(--text-tertiary)' }} />
        )}
        {running && <span className="text-[12px] font-medium tabular-nums" style={{ color: '#C9A227' }}>{fmt(elapsed)}</span>}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.16, ease: [0.22, 0.61, 0.36, 1] }}
            className="absolute right-0 mt-2 w-[300px] rounded-[12px] overflow-hidden z-[120]"
            style={{ background: 'var(--widget-bg)', border: '1px solid var(--border-soft)', boxShadow: '0 18px 50px rgba(0,0,0,0.4)' }}
          >
            <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid var(--border-color)' }}>
              <span className="text-[12px] uppercase tracking-[0.16em]" style={{ color: 'var(--text-tertiary)' }}>Chrono</span>
              <button onClick={() => setOpen(false)} style={{ color: 'var(--text-tertiary)' }}><X className="w-3.5 h-3.5" /></button>
            </div>

            <div className="px-4 py-4">
              <div className="text-center font-serif tabular-nums mb-4" style={{ fontSize: 34, letterSpacing: '-0.02em', color: running ? '#C9A227' : 'var(--text-primary)' }}>
                {fmt(elapsed)}
              </div>

              <div className="space-y-2.5">
                <select
                  value={dossierId}
                  onChange={(e) => { setDossierId(e.target.value); persist({ dossierId: e.target.value }); }}
                  className="w-full px-3 py-2 rounded-[8px] text-[12px] outline-none"
                  style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                >
                  <option value="">Dossier (optionnel)</option>
                  {dossiers.map((d) => <option key={d.id} value={d.id}>{d.numero_dossier} · {(d.intitule || '').substring(0, 28)}</option>)}
                </select>
                <input
                  value={description}
                  onChange={(e) => { setDescription(e.target.value); persist({ description: e.target.value }); }}
                  placeholder="Nature de la prestation…"
                  className="w-full px-3 py-2 rounded-[8px] text-[12px] outline-none"
                  style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                />
                <button
                  onClick={() => { setFacturable((f) => { persist({ facturable: !f }); return !f; }); }}
                  className="w-full flex items-center justify-between px-3 py-2 rounded-[8px] text-[12px]"
                  style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', color: 'var(--text-secondary)' }}
                >
                  <span>Facturable</span>
                  <span className="w-9 h-5 rounded-full relative transition-colors" style={{ background: facturable ? 'rgba(34,197,94,0.5)' : 'var(--border-soft)' }}>
                    <span className="absolute top-0.5 w-4 h-4 rounded-full transition-all" style={{ left: facturable ? 18 : 2, background: '#fff' }} />
                  </span>
                </button>
              </div>

              <div className="mt-4">
                {!running ? (
                  <button onClick={start} className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-[8px] text-[13px] font-medium" style={{ background: 'var(--text-primary)', color: 'var(--bg-primary)' }} data-testid="quick-timer-start">
                    <Play className="w-4 h-4" /> Démarrer
                  </button>
                ) : (
                  <div className="flex items-center gap-2">
                    <button onClick={stopAndSave} disabled={saving} className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-[8px] text-[13px] font-medium" style={{ background: '#C9A227', color: '#1a1714', opacity: saving ? 0.6 : 1 }} data-testid="quick-timer-stop">
                      <Square className="w-3.5 h-3.5" /> {saving ? 'Enregistrement…' : 'Arrêter et enregistrer'}
                    </button>
                    <button onClick={reset} className="px-3 py-2.5 rounded-[8px] text-[12px]" style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', color: 'var(--text-tertiary)' }} title="Abandonner">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
